import { RSU } from '_lib/api';
import { useAppDispatch } from '_store';
import { devicesSagas } from '_store/devices/sagas';
import { devicesSelector } from '_store/devices/selectors';
import { selectedRoadSegmentIdsSelector } from '_store/roads/selectors';
import { useLocalization } from '@fluent/react';
import { CardContent, IconButton, InputAdornment, TextField } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useRolePermissions } from '../../../hooks/useRolePermissions';
import MessageDialog from '../../items/MessageDialog';
import SelectItem from '../../items/SelectItem';
import CardListItem from './Card';
import styles from './styles';

export interface DeviceLayoutProps {
  onUpdateRsu?: (idExpanded: string) => void;
}

// expanded details inside RSU list items
const DeviceLayout: FC<DeviceLayoutProps> = (crudProps) => {
  const { l10n } = useLocalization();
  const classes = styles();
  const dispatch = useAppDispatch();
  const { canManageRsu, blockedActionMode } = useRolePermissions();

  const devices = useSelector(devicesSelector);
  const selectedRoadSegmentIds = useSelector(selectedRoadSegmentIdsSelector);
  const writeBlockedMessage = l10n.getString('forms-rbac-readonly-action-denied');

  const [filter, setFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState<string | null>('all');
  const [showMessage, setShowMessage] = useState({
    active: false,
    typeAction: 'delete',
    idToDelete: null,
    typeEvent: 'rsu',
  });

  useEffect(() => {
    let activeParam;
    if (activeFilter === 'true') {
      activeParam = true;
    } else if (activeFilter === 'false') {
      activeParam = false;
    } else {
      activeParam = undefined;
    }

    dispatch(
      devicesSagas.actions.devicesRequest({
        roadSegmentIds: selectedRoadSegmentIds,
        active: activeParam,
      }),
    );
  }, [dispatch, activeFilter, selectedRoadSegmentIds]);

  const filterRSU = (val: RSU) => {
    const searchVal = filter.toLowerCase();
    const itemVal = val.name.toString().toLowerCase();
    return itemVal.indexOf(searchVal) > -1;
  };

  const onMessageLeaveConfirmed = (type: string) => {
    if (type === 'confirm') {
      dispatch(devicesSagas.actions.deleteRSU(showMessage.idToDelete));
    }

    setShowMessage((sm) => ({
      ...sm,
      active: false,
      idToDelete: null,
    }));
  };

  const onDeleteRsu = (id: string) => {
    setShowMessage((sm) => ({
      ...sm,
      active: true,
      idToDelete: id,
      count: 1,
    }));
  };

  const statusOptions = [
    { label: l10n.getString('forms-rsu-status-all'), value: 'all' },
    { label: l10n.getString('forms-rsu-status-active'), value: 'true' },
    { label: l10n.getString('forms-rsu-status-inactive'), value: 'false' },
  ];

  const handleStatusChange = (event) => {
    setActiveFilter(event.target.value);
  };

  return (
    <CardContent className={classes.cardContent} style={{ overflow: 'hidden' }}>
      {showMessage.active === true ? (
        <MessageDialog statusMessage={showMessage} onCloseDialog={onMessageLeaveConfirmed} />
      ) : null}

      <div>
        <TextField
          className={classes.input}
          key={'search'}
          label={l10n.getString('forms-rsu-search')}
          InputLabelProps={{ variant: 'standard' }}
          onChange={(ev) => setFilter(ev.target.value)}
          fullWidth
          type="text"
          value={filter}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton edge="start">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),

            endAdornment: filter && (
              <IconButton aria-label="toggle password visibility" onClick={() => setFilter('')}>
                <CloseIcon />
              </IconButton>
            ),
          }}
        />
      </div>
      <div>
        <SelectItem
          text={l10n.getString('forms-rsu-status')}
          options={statusOptions}
          value={activeFilter}
          onChange={handleStatusChange}
          formControlStyle={{ minWidth: 150, marginTop: 0, marginBottom: 10 }}
          tooltip={l10n.getString('forms-rsu-status-tooltip')}
        />
      </div>

      <div style={{ overflow: 'auto' }}>
        {devices &&
          devices
            .filter(filterRSU)
            .map((item) => (
              <CardListItem
                asCard={true}
                key={item.id}
                device={item}
                text={item.name}
                onUpdate={() => crudProps.onUpdateRsu?.(item.id)}
                onDelete={() => onDeleteRsu(item.id)}
                writeAllowed={canManageRsu}
                blockedActionMode={blockedActionMode}
                blockedMessage={writeBlockedMessage}
              />
            ))}
      </div>
    </CardContent>
  );
};

export default React.memo(DeviceLayout);
